import { useState } from "react";
import {
  CUSTOMER_ITEM_TYPE_OPTIONS,
  CUSTOMER_SERVICE_OPTIONS,
  ITEM_TYPE_LABELS_FA,
  SERVICE_LABELS_FA,
  type CarpetType,
  type MatchingFlowType,
  type ServiceCatalogKey,
  type ServiceRequestSummary,
  type Wave2Bundle,
} from "@ghalishor/contracts";
import {
  formatJalaliDateTime,
  JALALI_MONTHS_FA,
  nowJalaliParts,
  toFaDigits,
} from "./jalali";
import { occasionsFor } from "./jalali-occasions";
import { BrandLogo } from "./brand-logo";

const PRIMARY_ITEMS = [
  "handmade",
  "machine_made",
  "moket",
  "sofa",
  "blanket",
  "other",
] as const;
const PRIMARY_SERVICES = [
  "washing_standard",
  "washing_premium",
  "dusting",
  "stain_removal",
  "washing_sofa",
  "washing_blanket",
] as const;

const countUnitForItem = (itemType: CarpetType): string => {
  if (itemType === "sofa") return "دست/سرویس";
  if (itemType === "curtain" || itemType === "mattress" || itemType === "other")
    return "عدد";
  return "تخته";
};

export function wave2BundleLabelFa(
  bundle: Wave2Bundle | string | null | undefined,
): string | null {
  switch (bundle) {
    case "faster":
      return "تحویل سریع‌تر";
    case "economical":
      return "انتخاب اقتصادی";
    case "higher_guarantee":
      return "ضمانت بالاتر";
    default:
      return null;
  }
}

const LIVE_STATUSES = new Set([
  "draft",
  "submitted",
  "matching",
  "offered",
  "waiting_acceptance",
  "rejected_by_business",
]);

const ACCEPTED_STATUSES = new Set([
  "accepted",
  "contact_pending",
  "price_pending",
  "schedule_pending",
  "scheduled",
]);

export interface CustomerRequestFormValues {
  readonly carpetCount: string;
  readonly approximateSqm: string;
  readonly selectedServices: readonly ServiceCatalogKey[];
  readonly maxReturnWaitDays: string;
}

export function validateCustomerRequestForm(
  values: CustomerRequestFormValues,
  step: 1 | 2 = 2,
): readonly string[] {
  const errors: string[] = [];
  const count = Number(values.carpetCount);
  const sqm = Number(values.approximateSqm);
  if (!Number.isInteger(count) || count < 1)
    errors.push("تعداد باید یک عدد صحیح و حداقل یک باشد.");
  if (!Number.isFinite(sqm) || sqm <= 0)
    errors.push("متراژ تقریبی باید بیشتر از صفر باشد.");
  if (step === 1) return errors;

  if (values.selectedServices.length === 0)
    errors.push("حداقل یک خدمت را انتخاب کنید.");
  const waitDays = Number(values.maxReturnWaitDays);
  if (!Number.isInteger(waitDays) || waitDays < 1 || waitDays > 14) {
    errors.push("زمان برگشت باید بین ۱ تا ۱۴ روز باشد.");
  }
  return errors;
}

export function groupCustomerRequests(
  requests: readonly ServiceRequestSummary[],
) {
  return Object.freeze({
    live: requests.filter((request) => LIVE_STATUSES.has(request.status)),
    accepted: requests.filter((request) =>
      ACCEPTED_STATUSES.has(request.status),
    ),
    closed: requests.filter(
      (request) =>
        request.status === "completed" || request.status === "cancelled",
    ),
  });
}

export function requestNextAction(
  status: ServiceRequestSummary["status"],
): string {
  switch (status) {
    case "draft":
      return "اطلاعات را کامل کنید و درخواست را ارسال کنید.";
    case "submitted":
    case "matching":
      return "در حال یافتن قالیشویی مناسب هستیم؛ نیاز به اقدام شما نیست.";
    case "offered":
      return "پیشنهادها آماده‌اند؛ قالیشویی مناسب را انتخاب کنید.";
    case "waiting_acceptance":
      return "منتظر پذیرش قالیشویی هستیم؛ نتیجه همین‌جا نمایش داده می‌شود.";
    case "rejected_by_business":
      return "این پیشنهاد پذیرفته نشد؛ برای یافتن گزینه بعدی ادامه دهید.";
    case "accepted":
    case "contact_pending":
      return "اطلاعات تماس و هماهنگی جمع‌آوری را بررسی کنید.";
    case "price_pending":
      return "قیمت نهایی را بررسی و تأیید یا رد کنید.";
    case "schedule_pending":
      return "زمان پیشنهادی جمع‌آوری و برگشت را بررسی کنید.";
    case "scheduled":
      return "سفارش زمان‌بندی شده است؛ جزئیات جمع‌آوری را دنبال کنید.";
    case "completed":
      return "سفارش تکمیل شده؛ می‌توانید تجربه خود را ثبت کنید.";
    case "cancelled":
      return "این درخواست لغو شده است.";
  }
}

function requestJourneyIndex(status: ServiceRequestSummary["status"]): number {
  if (status === "completed") return 4;
  if (status === "scheduled") return 3;
  if (
    [
      "accepted",
      "contact_pending",
      "price_pending",
      "schedule_pending",
    ].includes(status)
  )
    return 2;
  if (
    [
      "submitted",
      "matching",
      "offered",
      "waiting_acceptance",
      "rejected_by_business",
    ].includes(status)
  )
    return 1;
  return 0;
}

const WEEKDAYS_FA = [
  "یکشنبه",
  "دوشنبه",
  "سه‌شنبه",
  "چهارشنبه",
  "پنجشنبه",
  "جمعه",
  "شنبه",
] as const;

export function statusLabelFa(
  status: ServiceRequestSummary["status"] | string,
): string {
  switch (status) {
    case "draft":
      return "پیش‌نویس";
    case "submitted":
      return "ارسال‌شده";
    case "matching":
      return "در حال یافتن قالیشویی";
    case "offered":
      return "پیشنهاد آماده";
    case "waiting_acceptance":
      return "منتظر پذیرش";
    case "rejected_by_business":
      return "رد شده";
    case "accepted":
      return "پذیرفته شد";
    case "contact_pending":
      return "هماهنگی تماس";
    case "price_pending":
      return "تأیید قیمت";
    case "schedule_pending":
      return "تأیید زمان";
    case "scheduled":
      return "زمان‌بندی شد";
    case "completed":
      return "تکمیل شد";
    case "cancelled":
      return "لغو شده";
    default:
      return status;
  }
}

export function TodayCalendarCard(props: { readonly compact?: boolean } = {}) {
  const now = new Date();
  const j = nowJalaliParts();
  const occasions = occasionsFor(j.jm, j.jd);
  const weekday = WEEKDAYS_FA[now.getDay()] ?? "";
  const month = JALALI_MONTHS_FA[j.jm - 1] ?? "";

  return (
    <div
      className={`today-calendar-card${props.compact ? " today-calendar-card-compact" : ""}`}
      aria-label="تقویم امروز"
    >
      <div className="today-calendar-day">
        <strong>{toFaDigits(j.jd)}</strong>
        <span>{month}</span>
      </div>
      <div className="today-calendar-meta">
        <strong>
          {weekday} {toFaDigits(j.jd)} {month} {toFaDigits(j.jy)}
        </strong>
        {occasions.length === 0 ? (
          <p>امروز مناسبت ویژه‌ای در تقویم ثبت نشده است.</p>
        ) : (
          occasions.map((o) => (
            <p
              key={o.title}
              className={o.isHoliday ? "occasion-holiday" : "occasion-normal"}
            >
              {o.isHoliday ? `تعطیل رسمی · ${o.title}` : o.title}
            </p>
          ))
        )}
      </div>
    </div>
  );
}

export function CustomerHomeHero(props: {
  readonly mobile: string | null | undefined;
  readonly onNewRequest: () => void;
  readonly onMyRequests: () => void;
  readonly onMessages: () => void;
  readonly onDownloadApps?: () => void;
}) {
  return (
    <div className="customer-home-stack">
      <div className="customer-hero">
        <p className="brand-mark">
          <BrandLogo />
        </p>
        <h1 className="hero-title">فرش‌تان را بسپارید؛ بقیه‌اش با ما.</h1>
        <p className="hero-lead">
          در چند گام کوتاه درخواست بسازید؛ نزدیک‌ترین قالیشویی‌ها پیدا می‌شوند و
          هماهنگی را خودتان می‌بینید.
        </p>
        <div className="cta-row">
          <button
            type="button"
            className="cta-primary"
            onClick={props.onNewRequest}
          >
            ثبت درخواست شستشو
          </button>
          <button
            type="button"
            className="cta-ghost"
            onClick={props.onMyRequests}
          >
            پیگیری درخواست‌ها
          </button>
          <button
            type="button"
            className="cta-ghost"
            onClick={props.onMessages}
          >
            پیام‌ها و پشتیبانی
          </button>
          {props.onDownloadApps ? (
            <button
              type="button"
              className="cta-ghost"
              onClick={props.onDownloadApps}
            >
              دانلود اپ اندروید
            </button>
          ) : null}
        </div>
        {props.mobile ? (
          <p className="hero-meta">وارد شده با {props.mobile}</p>
        ) : null}
      </div>
      <TodayCalendarCard />
    </div>
  );
}

function RequestSection(props: {
  readonly title: string;
  readonly subtitle: string;
  readonly band: "live" | "accepted" | "closed";
  readonly items: readonly ServiceRequestSummary[];
  readonly onOpen: (id: string, draft: boolean) => void;
}) {
  if (props.items.length === 0) return null;
  return (
    <section className={`request-section request-section-${props.band}`}>
      <header className="request-section-head">
        <h2>{props.title}</h2>
        <p>{props.subtitle}</p>
      </header>
      <div className="request-list polished">
        {props.items.map((item) => {
          const isDraft = item.status === "draft";
          const when = item.submittedAt ?? item.updatedAt ?? item.createdAt;
          const wave2Label = wave2BundleLabelFa(item.wave2Bundle);
          return (
            <button
              type="button"
              className={`request-card request-card-${props.band}`}
              key={item.id}
              onClick={() => props.onOpen(item.id, isDraft)}
            >
              <div className="request-card-top">
                <strong>
                  {ITEM_TYPE_LABELS_FA[item.carpetType] ?? item.carpetType} ·{" "}
                  {item.carpetCount.toLocaleString("fa-IR")}{" "}
                  {countUnitForItem(item.carpetType)}
                </strong>
                <span className={`status-pill status-${item.status}`}>
                  {statusLabelFa(item.status)}
                </span>
              </div>
              <span className="request-card-meta">
                {item.approximateSqm.toLocaleString("fa-IR")} مترمربع ·{" "}
                {formatJalaliDateTime(when)}
              </span>
              {wave2Label ? (
                <span className="request-card-priority">
                  ✓ اولویت ثبت‌شده: {wave2Label}
                </span>
              ) : null}
              <span className="request-card-action">
                {isDraft ? "ادامه ویرایش" : "مشاهده وضعیت"}
              </span>
            </button>
          );
        })}
      </div>
    </section>
  );
}

export function CustomerRequestList(props: {
  readonly requests: readonly ServiceRequestSummary[];
  readonly onOpen: (id: string, draft: boolean) => void;
  readonly onNew: () => void;
  readonly emptyHint?: string;
}) {
  if (props.requests.length === 0) {
    return (
      <div className="empty-state">
        <strong>هنوز درخواستی ندارید</strong>
        <p>{props.emptyHint ?? "اولین درخواست را همین حالا ثبت کنید."}</p>
        <button type="button" className="cta-primary" onClick={props.onNew}>
          ثبت درخواست
        </button>
      </div>
    );
  }

  const { live, accepted, closed } = groupCustomerRequests(props.requests);

  return (
    <div className="request-sections">
      <RequestSection
        title="در انتظار پذیرش"
        subtitle="هنوز هیچ قالیشویی این سفارش را نپذیرفته یا در حال یافتن است"
        band="live"
        items={live}
        onOpen={props.onOpen}
      />
      <RequestSection
        title="پذیرفته‌شده و در جریان"
        subtitle="قالیشویی مشخص شده؛ هماهنگی و انجام سفارش"
        band="accepted"
        items={accepted}
        onOpen={props.onOpen}
      />
      <RequestSection
        title="پایان‌یافته"
        subtitle="سفارش‌های انجام‌شده یا لغوشده"
        band="closed"
        items={closed}
        onOpen={props.onOpen}
      />
    </div>
  );
}

export function CustomerRequestProgress(props: {
  readonly request: ServiceRequestSummary;
}) {
  const stages = [
    "ثبت درخواست",
    "انتخاب قالیشویی",
    "هماهنگی",
    "تکمیل",
  ] as const;
  const activeIndex = requestJourneyIndex(props.request.status);
  const cancelled = props.request.status === "cancelled";
  return (
    <section
      className={`request-progress${cancelled ? " request-progress-cancelled" : ""}`}
      aria-label="روند درخواست"
    >
      <div className="request-progress-head">
        <div>
          <span>اقدام بعدی</span>
          <strong>{requestNextAction(props.request.status)}</strong>
        </div>
        <span className={`status-pill status-${props.request.status}`}>
          {statusLabelFa(props.request.status)}
        </span>
      </div>
      <ol className="request-progress-steps">
        {stages.map((stage, index) => {
          const completed = !cancelled && index < activeIndex;
          const active = !cancelled && index === activeIndex;
          return (
            <li
              key={stage}
              className={completed ? "completed" : active ? "active" : ""}
              aria-current={active ? "step" : undefined}
            >
              <span aria-hidden="true">
                {completed ? "✓" : toFaDigits(index + 1)}
              </span>
              <strong>{stage}</strong>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export function CustomerRequestWizard(props: {
  readonly isEdit: boolean;
  readonly busy: boolean;
  readonly error: string | null;
  readonly info: string | null;
  readonly carpetType: CarpetType;
  readonly carpetCount: string;
  readonly approximateSqm: string;
  readonly selectedServices: readonly ServiceCatalogKey[];
  readonly notes: string;
  readonly maxReturnWaitDays: string;
  readonly flowType: MatchingFlowType;
  readonly onCarpetType: (v: CarpetType) => void;
  readonly onCarpetCount: (v: string) => void;
  readonly onApproximateSqm: (v: string) => void;
  readonly onToggleService: (key: ServiceCatalogKey) => void;
  readonly onNotes: (v: string) => void;
  readonly onMaxReturnWaitDays: (v: string) => void;
  readonly onFlowType: (v: MatchingFlowType) => void;
  readonly onSaveDraft: () => void;
  readonly onSubmit: () => void;
  readonly onBack: () => void;
}) {
  const [step, setStep] = useState(1);
  const [showMoreItems, setShowMoreItems] = useState(false);
  const [showMoreServices, setShowMoreServices] = useState(false);
  const [validationErrors, setValidationErrors] = useState<readonly string[]>(
    [],
  );

  const itemOptions = (
    CUSTOMER_ITEM_TYPE_OPTIONS as readonly CarpetType[]
  ).filter((key) =>
    showMoreItems ? true : (PRIMARY_ITEMS as readonly string[]).includes(key),
  );
  const serviceOptions = (
    CUSTOMER_SERVICE_OPTIONS as readonly ServiceCatalogKey[]
  ).filter((key) =>
    showMoreServices
      ? true
      : (PRIMARY_SERVICES as readonly string[]).includes(key),
  );

  const formValues: CustomerRequestFormValues = {
    carpetCount: props.carpetCount,
    approximateSqm: props.approximateSqm,
    selectedServices: props.selectedServices,
    maxReturnWaitDays: props.maxReturnWaitDays,
  };

  const continueToServices = () => {
    const errors = validateCustomerRequestForm(formValues, 1);
    setValidationErrors(errors);
    if (errors.length === 0) setStep(2);
  };

  const performValidated = (action: () => void) => {
    const errors = validateCustomerRequestForm(formValues, 2);
    setValidationErrors(errors);
    if (errors.length === 0) action();
  };

  return (
    <div className="wizard">
      <div className="wizard-steps" aria-label="مراحل">
        <span className={step === 1 ? "active" : "completed"}>۱ · کالا</span>
        <span className="wizard-sep" />
        <span className={step === 2 ? "active" : ""}>۲ · خدمات و ارسال</span>
      </div>

      {step === 1 ? (
        <div className="wizard-pane">
          <h2 className="wizard-heading">چه چیزی می‌خواهید بسپارید؟</h2>
          <div className="chip-grid">
            {itemOptions.map((key) => (
              <button
                type="button"
                key={key}
                className={props.carpetType === key ? "chip selected" : "chip"}
                aria-pressed={props.carpetType === key}
                onClick={() => props.onCarpetType(key)}
              >
                {ITEM_TYPE_LABELS_FA[key]}
              </button>
            ))}
          </div>
          {!showMoreItems ? (
            <button
              type="button"
              className="link-button"
              onClick={() => setShowMoreItems(true)}
            >
              انواع بیشتر…
            </button>
          ) : null}
          <div className="metric-row">
            <label>
              تعداد
              <input
                type="number"
                min={1}
                step={1}
                value={props.carpetCount}
                onChange={(e) => props.onCarpetCount(e.target.value)}
                required
              />
            </label>
            <label>
              متراژ تقریبی (م²)
              <input
                type="number"
                min={0.01}
                step={0.01}
                value={props.approximateSqm}
                onChange={(e) => props.onApproximateSqm(e.target.value)}
                required
              />
            </label>
          </div>
          {validationErrors.length > 0 ? (
            <div className="form-errors" role="alert">
              {validationErrors.map((message) => (
                <span key={message}>{message}</span>
              ))}
            </div>
          ) : null}
          <div className="cta-row wizard-actions">
            <button
              type="button"
              className="cta-primary"
              onClick={continueToServices}
            >
              ادامه به انتخاب خدمات
            </button>
            <button type="button" className="secondary" onClick={props.onBack}>
              انصراف
            </button>
          </div>
        </div>
      ) : (
        <div className="wizard-pane">
          <h2 className="wizard-heading">چه خدماتی لازم دارید؟</h2>
          <div className="service-checks">
            {serviceOptions.map((key) => (
              <label
                className={
                  props.selectedServices.includes(key)
                    ? "check-card selected"
                    : "check-card"
                }
                key={key}
              >
                <input
                  type="checkbox"
                  checked={props.selectedServices.includes(key)}
                  onChange={() => props.onToggleService(key)}
                />
                <span>{SERVICE_LABELS_FA[key]}</span>
              </label>
            ))}
          </div>
          {!showMoreServices ? (
            <button
              type="button"
              className="link-button"
              onClick={() => setShowMoreServices(true)}
            >
              خدمات بیشتر…
            </button>
          ) : null}
          <label className="notes-label">
            یادداشت برای قالیشویی (اختیاری)
            <textarea
              value={props.notes}
              onChange={(e) => props.onNotes(e.target.value)}
              rows={2}
            />
          </label>
          <label className="notes-label">
            حداکثر انتظار برای برگشت فرش (روز)
            <input
              type="number"
              min={1}
              max={14}
              step={1}
              value={props.maxReturnWaitDays}
              onChange={(e) => props.onMaxReturnWaitDays(e.target.value)}
              required
            />
            <span className="field-hint">
              قالیشویی‌هایی که معمولاً زودتر برمی‌گردانند در اولویت ارجاع قرار
              می‌گیرند.
            </span>
            <span className="quick-picks" aria-label="انتخاب سریع زمان برگشت">
              {[3, 5, 7, 10].map((days) => (
                <button
                  key={days}
                  type="button"
                  className={
                    Number(props.maxReturnWaitDays) === days ? "selected" : ""
                  }
                  aria-pressed={Number(props.maxReturnWaitDays) === days}
                  onClick={() => props.onMaxReturnWaitDays(String(days))}
                >
                  {toFaDigits(days)} روز
                </button>
              ))}
            </span>
          </label>
          <details className="advanced-details">
            <summary>نحوه یافتن قالیشویی</summary>
            <div className="choice-group soft">
              <label className="choice-row">
                <input
                  type="radio"
                  name="flowType"
                  checked={props.flowType === "auto_broadcast"}
                  onChange={() => props.onFlowType("auto_broadcast")}
                />
                ارسال خودکار به نزدیک‌ترین‌ها (پیشنهادی)
              </label>
              <label className="choice-row">
                <input
                  type="radio"
                  name="flowType"
                  checked={props.flowType === "curated_offers"}
                  onChange={() => props.onFlowType("curated_offers")}
                />
                خودم از بین پیشنهادها انتخاب کنم
              </label>
            </div>
          </details>
          <section className="request-review" aria-label="خلاصه درخواست">
            <header>
              <div>
                <span>{props.isEdit ? "خلاصه ویرایش" : "خلاصه درخواست"}</span>
                <strong>{ITEM_TYPE_LABELS_FA[props.carpetType]}</strong>
              </div>
              <button
                type="button"
                className="link-button"
                onClick={() => setStep(1)}
              >
                ویرایش کالا
              </button>
            </header>
            <dl>
              <div>
                <dt>مقدار</dt>
                <dd>
                  {toFaDigits(props.carpetCount)}{" "}
                  {countUnitForItem(props.carpetType)} ·{" "}
                  {toFaDigits(props.approximateSqm)} مترمربع
                </dd>
              </div>
              <div>
                <dt>خدمات</dt>
                <dd>
                  {props.selectedServices
                    .map((key) => SERVICE_LABELS_FA[key])
                    .join("، ") || "انتخاب نشده"}
                </dd>
              </div>
              <div>
                <dt>زمان برگشت</dt>
                <dd>حداکثر {toFaDigits(props.maxReturnWaitDays)} روز</dd>
              </div>
              <div>
                <dt>نحوه انتخاب</dt>
                <dd>
                  {props.flowType === "auto_broadcast"
                    ? "پیشنهاد سریع خودکار"
                    : "انتخاب از بین پیشنهادها"}
                </dd>
              </div>
            </dl>
          </section>
          {validationErrors.length > 0 ? (
            <div className="form-errors" role="alert">
              {validationErrors.map((message) => (
                <span key={message}>{message}</span>
              ))}
            </div>
          ) : null}
          {props.info ? <div className="message info">{props.info}</div> : null}
          {props.error ? (
            <div className="message error">{props.error}</div>
          ) : null}
          <div className="cta-row wizard-actions">
            <button
              type="button"
              className="cta-primary"
              disabled={props.busy}
              onClick={() => performValidated(props.onSubmit)}
            >
              {props.busy
                ? "در حال ارسال…"
                : props.isEdit
                  ? "ذخیره و ارسال درخواست"
                  : "تأیید و ارسال درخواست"}
            </button>
            <button
              type="button"
              className="secondary"
              disabled={props.busy}
              onClick={() => performValidated(props.onSaveDraft)}
            >
              ذخیره پیش‌نویس
            </button>
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setValidationErrors([]);
                setStep(1);
              }}
            >
              بازگشت به مرحله قبل
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
